import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import apiService, { type PermissionDef } from "../services/api";

/**
 * "Acceso (nuevo)": the unified permission model, edited side by side with "Acceso a Productos".
 * It only writes `Company.permissions` / `User.permissions`; for now only the chat reads them.
 */

const GROUPS: Array<{ group: PermissionDef["group"]; label: string }> = [
  { group: "explorer", label: "Explorador" },
  { group: "product", label: "Productos" },
  { group: "chat", label: "Chat" },
];

function requirementsMet(
  def: PermissionDef,
  granted: ReadonlySet<string>,
): boolean {
  if (def.requiresAll && !def.requiresAll.every((id) => granted.has(id)))
    return false;
  if (def.requiresAny && !def.requiresAny.some((id) => granted.has(id)))
    return false;
  return true;
}

/** Same rule as the backend: drops unknown, unavailable and unmet ids (turning a module off clears its dependents). */
function normalize(ids: Iterable<string>, catalog: PermissionDef[]): string[] {
  const granted = new Set(ids);
  return catalog
    .filter(
      (d) =>
        granted.has(d.id) &&
        d.available !== false &&
        requirementsMet(d, granted),
    )
    .map((d) => d.id);
}

function requirementText(def: PermissionDef, catalog: PermissionDef[]): string {
  const label = (id: string) => catalog.find((d) => d.id === id)?.label ?? id;
  const parts = [
    ...(def.requiresAll ?? []).map(label),
    ...(def.requiresAny?.length
      ? [def.requiresAny.map(label).join(" o ")]
      : []),
  ];
  return parts.length ? `Requiere ${parts.join(" + ")}` : "";
}

const sameIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

const PermissionChecklist: React.FC<{
  catalog: PermissionDef[];
  /** Ids that can be offered at all (the company list, for a user). */
  offered?: ReadonlySet<string>;
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}> = ({ catalog, offered, value, onChange, disabled }) => {
  const granted = new Set(value);
  const toggle = (id: string, on: boolean) =>
    onChange(
      normalize(on ? [...value, id] : value.filter((v) => v !== id), catalog),
    );
  return (
    <Stack spacing={2}>
      {GROUPS.map(({ group, label }) => {
        const defs = catalog.filter(
          (d) =>
            d.group === group &&
            (!offered || offered.has(d.id) || d.available === false),
        );
        if (defs.length === 0) return null;
        return (
          <Box key={group}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {label}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 2,
              }}
            >
              {defs.map((def) => {
                const unavailable = def.available === false;
                const blocked = !unavailable && !requirementsMet(def, granted);
                const hint = unavailable
                  ? def.description
                  : blocked
                    ? requirementText(def, catalog)
                    : def.description;
                return (
                  <Tooltip
                    key={def.id}
                    title={hint ?? ""}
                    placement="top-start"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={granted.has(def.id)}
                          disabled={disabled || unavailable || blocked}
                          onChange={(e) => toggle(def.id, e.target.checked)}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{def.label}</Typography>
                          {unavailable && (
                            <Chip size="small" label="Próximamente" />
                          )}
                        </Stack>
                      }
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * The Chat product row for "Acceso a Productos". It toggles the `chat` id of "Acceso (nuevo)" (the
 * only source the chat reads), never the legacy `products`; a company without a stored list gets
 * one, starting from the derived ids.
 */
export const ChatProductToggle: React.FC<{
  companyId: string;
  icon: React.ReactNode;
}> = ({ companyId, icon }) => {
  const queryClient = useQueryClient();
  const queryKey = ["company-permissions", companyId];
  const { data: overview } = useQuery({
    queryKey,
    queryFn: () => apiService.getCompanyPermissions(companyId),
  });
  const save = useMutation({
    mutationFn: (ids: string[]) =>
      apiService.updateCompanyPermissions(companyId, ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
  const enabled = overview?.effective.includes("chat") ?? false;
  const busy = !overview || save.isPending;
  const toggle = () => {
    if (!overview || save.isPending) return;
    const ids = enabled
      ? overview.effective.filter((id) => id !== "chat")
      : [...overview.effective, "chat"];
    save.mutate(normalize(ids, overview.catalog));
  };
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        cursor: busy ? "default" : "pointer",
        bgcolor: enabled ? "action.selected" : "transparent",
        borderColor: enabled ? "primary.main" : "divider",
        "&:hover": { bgcolor: "action.hover" },
        transition: "all 0.2s",
      }}
      onClick={toggle}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Checkbox checked={enabled} disabled={busy} sx={{ p: 0 }} />
        {icon}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Chat
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Asistente de chat. Se guarda en Acceso (nuevo); las acciones del
            chat se eligen ahí.
          </Typography>
          {save.error && (
            <Typography variant="caption" color="error">
              No se pudo guardar.
            </Typography>
          )}
        </Box>
        {save.isPending ? (
          <CircularProgress size={16} />
        ) : (
          enabled && <Chip label="Activo" color="primary" size="small" />
        )}
      </Stack>
    </Paper>
  );
};

const CompanyPermissionsPanel: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const queryClient = useQueryClient();
  const queryKey = ["company-permissions", companyId];
  const overviewQuery = useQuery({
    queryKey,
    queryFn: () => apiService.getCompanyPermissions(companyId),
  });
  const overview = overviewQuery.data;
  const catalog = useMemo(() => overview?.catalog ?? [], [overview]);

  const [draft, setDraft] = useState<string[]>([]);
  useEffect(() => {
    if (overview) setDraft(overview.effective);
  }, [overview]);

  const [editingUser, setEditingUser] = useState<{
    userId: string;
    name: string;
    ids: string[];
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const saveCompany = useMutation({
    mutationFn: (ids: string[]) =>
      apiService.updateCompanyPermissions(companyId, ids),
    onSuccess: () => {
      setNotice("Permisos de la empresa guardados.");
      void refresh();
    },
  });
  const saveUser = useMutation({
    mutationFn: ({ userId, ids }: { userId: string; ids: string[] }) =>
      apiService.updateUserPermissions(companyId, userId, ids),
    onSuccess: () => {
      setEditingUser(null);
      void refresh();
    },
  });
  const resync = useMutation({
    mutationFn: () => apiService.resyncCompanyPermissions(companyId),
    onSuccess: (result) => {
      setNotice(
        `Re-sincronizado desde Acceso a Productos: empresa ${result.companyChanged ? "actualizada" : "sin cambios"}, ${result.usersChanged} usuario(s) actualizados, ${result.mismatches.length} diferencia(s) de paridad.`,
      );
      void refresh();
    },
  });

  if (overviewQuery.isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }
  if (!overview) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">No pudimos cargar los permisos (nuevo).</Alert>
      </Paper>
    );
  }

  const companySet = new Set(overview.effective);
  const dirty =
    !sameIds(normalize(draft, catalog), overview.effective) ||
    overview.stored === null;
  const error = saveCompany.error ?? saveUser.error ?? resync.error;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Acceso (nuevo)</Typography>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="En prueba"
        />
        {overview.mismatches === 0 ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Paridad OK"
          />
        ) : (
          <Chip
            size="small"
            color="error"
            label={`${overview.mismatches} usuario(s) con diferencias`}
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Modelo unificado de permisos de app y chat. El chat usa solo estos
        permisos; el resto de la app sigue leyendo Acceso a Productos, que no se
        modifica desde aquí.
      </Typography>
      {overview.stored === null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Esta empresa aún no tiene permisos guardados, así que el chat no le da
          acceso. Se muestran los derivados de Acceso a Productos: revisa y
          guarda.
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.response?.data?.message ??
            "No pudimos guardar los permisos."}
        </Alert>
      ) : null}

      <PermissionChecklist
        catalog={catalog}
        value={draft}
        onChange={setDraft}
        disabled={saveCompany.isPending}
      />

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Tooltip title="Vuelve a copiar Explorador, Productos y Exportar desde Acceso a Productos. Los permisos de chat guardados se mantienen.">
          <span>
            <Button
              variant="outlined"
              disabled={resync.isPending}
              onClick={() => resync.mutate()}
            >
              {resync.isPending ? "Re-sincronizando…" : "Re-sincronizar"}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          disabled={!dirty || saveCompany.isPending}
          onClick={() => saveCompany.mutate(normalize(draft, catalog))}
        >
          {saveCompany.isPending ? "Guardando…" : "Guardar"}
        </Button>
      </Stack>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
        Usuarios
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Un usuario hereda los permisos de la empresa o los restringe a un
        subconjunto.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Usuario</TableCell>
            <TableCell>Acceso</TableCell>
            <TableCell>Paridad</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {overview.users.map((u) => {
            const status = u.stored?.length
              ? "Personalizado"
              : "Hereda empresa";
            return (
              <TableRow key={u.userId}>
                <TableCell>
                  <Typography variant="body2">{u.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={u.resolved
                      .map(
                        (id) => catalog.find((d) => d.id === id)?.label ?? id,
                      )
                      .join(", ")}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${status} · ${u.resolved.length} permisos`}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {u.parity ? (
                    <Tooltip
                      title={`Legacy: ${u.parity.expectedProducts.join(", ") || "—"}${u.parity.expectedExport ? " + export" : ""} · Nuevo: ${u.parity.actualProducts.join(", ") || "—"}${u.parity.actualExport ? " + export" : ""}`}
                    >
                      <Chip size="small" color="error" label="Difiere" />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label="OK"
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() =>
                      setEditingUser({
                        userId: u.userId,
                        name: u.name,
                        ids: u.stored?.length ? u.stored : overview.effective,
                      })
                    }
                  >
                    Editar
                  </Button>
                  {Boolean(u.stored?.length) && (
                    <Button
                      size="small"
                      disabled={saveUser.isPending}
                      onClick={() =>
                        saveUser.mutate({ userId: u.userId, ids: [] })
                      }
                    >
                      Heredar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Permisos de {editingUser?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Solo se pueden elegir permisos que la empresa tiene. Guardar todos
            los de la empresa equivale a heredar.
          </Typography>
          {editingUser && (
            <PermissionChecklist
              catalog={catalog}
              offered={companySet}
              value={editingUser.ids.filter((id) => companySet.has(id))}
              onChange={(ids) => setEditingUser({ ...editingUser, ids })}
              disabled={saveUser.isPending}
            />
          )}
        </DialogContent>
        <DialogActions>
          {editingUser &&
            normalize(
              editingUser.ids.filter((id) => companySet.has(id)),
              catalog,
            ).length === 0 && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mr: "auto", ml: 2 }}
              >
                Una lista vacía significa heredar la empresa; para quitar todo
                el acceso, desactiva el usuario.
              </Typography>
            )}
          <Button onClick={() => setEditingUser(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              saveUser.isPending ||
              !editingUser ||
              normalize(
                editingUser.ids.filter((id) => companySet.has(id)),
                catalog,
              ).length === 0
            }
            onClick={() => {
              if (!editingUser) return;
              const ids = normalize(
                editingUser.ids.filter((id) => companySet.has(id)),
                catalog,
              );
              saveUser.mutate({
                userId: editingUser.userId,
                ids: sameIds(ids, overview.effective) ? [] : ids,
              });
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CompanyPermissionsPanel;
